import React, { useState, useEffect, useCallback, useRef } from "react";
import { CheckCircle2, Circle, XCircle, ChevronRight, ClipboardCheck, Download, HardHat, History, Mail, Pencil, Plus, ShieldAlert, Trash2, Upload, UserCog, Users, X } from "lucide-react";
import { api, apiUpload, downloadFile } from "../lib/api";
import { BulkDeleteBar } from "../components/bulk/BulkDeleteBar";
import { ClientSection } from "../components/coordination/ClientSection";
import { CompanyVerify } from "../components/coordination/CompanyVerify";
import { DocRequestBar, DocStateSelect } from "../components/coordination/DocRequest";
import { DocumentButton } from "../components/DocumentButton";
import { RequirementsEditor, defaultRequirements } from "../components/coordination/RequirementsEditor";
import { SiteVerify } from "../components/coordination/SiteVerify";
import { SortMenu } from "../components/sorting/SortMenu";
import { newRowId, stripRowIds, withRowIds } from "../lib/rowKeys";
import { sortRows, useSortPreference } from "../lib/sorting";
import { useSelection } from "../components/bulk/useSelection";

const STATUS_TONE = { OK: { bg: "#E5F1EB", fg: "#1F8B4C", label: "OK", Icon: CheckCircle2 }, EXPIRED: { bg: "#FCE9E7", fg: "#D93025", label: "SCADUTO", Icon: XCircle }, MISSING: { bg: "#FEF3C7", fg: "#C68A0A", label: "MANCANTE", Icon: Circle } };


function StatusBadge({ status, size = 12 }) {
  const t = STATUS_TONE[status] || STATUS_TONE.MISSING;
  const I = t.Icon;
  return <span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 8px",borderRadius:12,background:t.bg,color:t.fg,fontSize:9.5,fontWeight:700,letterSpacing:.4,textTransform:"uppercase"}}><I size={size} /> {t.label}</span>;
}

function StatusDot({ status }) {
  const t = STATUS_TONE[status] || STATUS_TONE.MISSING;
  return <span style={{width:10,height:10,borderRadius:"50%",background:t.fg,display:"inline-block",flexShrink:0}} />;
}

const COORD_SORTS = [
  { key: "client_name", kind: "text", dir: "asc", label: "Committente A → Z", group: "Alfabetico" },
  { key: "client_name", kind: "text", dir: "desc", label: "Committente Z → A", group: "Alfabetico" },
  { key: "name", kind: "text", dir: "asc", label: "Cantiere A → Z", group: "Alfabetico" },
  { key: "name", kind: "text", dir: "desc", label: "Cantiere Z → A", group: "Alfabetico" },
  { key: "expired", kind: "number", dir: "desc", label: "Documenti scaduti prima", group: "Documentazione" },
  { key: "missing", kind: "number", dir: "desc", label: "Documentazione incompleta prima", group: "Documentazione" },
  { key: "ok", kind: "number", dir: "desc", label: "Documentazione completa prima", group: "Documentazione" },
  { key: "companies_count", kind: "number", dir: "desc", label: "Più imprese", group: "Imprese" },
  { key: "updated_at", kind: "date", dir: "desc", label: "Ultima modifica", group: "Data" },
];

function CoordinationModule({ user, initialSite = "", onClearInitial = () => {} }) {
  const [sites, setSites] = useState([]);
  const [openSite, setOpenSite] = useState(initialSite || null);
  const [detail, setDetail] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { selected, toggle, replace, clear } = useSelection();
  const { sort, apply } = useSortPreference("coordination", null);
  const ordered = sortRows(sites, sort?.key ? sort : { key: "client_name", kind: "text", dir: "asc" },
    (row, key) => ["expired", "missing", "ok"].includes(key) ? (row.status_counts?.[key.toUpperCase()] || 0)
      : key === "updated_at" ? (row.updated_at || row.created_at || "") : row[key]);

  const loadList = useCallback(() => api("/coordination").then(setSites).catch((e) => setErr(e.message)), []);
  useEffect(() => { loadList(); api("/safety/catalog").then(setCatalog).catch(() => {}); }, [loadList]);

  const loadDetail = useCallback(async (siteId) => {
    if (!siteId) { setDetail(null); return; }
    setLoading(true);
    try { setDetail(await api(`/coordination/${siteId}`)); } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { loadDetail(openSite); }, [openSite, loadDetail]);
  useEffect(() => { if (initialSite) { setOpenSite(initialSite); onClearInitial(); } }, [initialSite]); // eslint-disable-line react-hooks/exhaustive-deps

  if (openSite && detail) return <CoordinationDetail detail={detail} catalog={catalog} user={user} back={() => setOpenSite(null)} reload={() => loadDetail(openSite)} />;
  if (openSite && loading) return <div className="empty" style={{padding:80}}><HardHat size={30} color="var(--wine)" /><strong>Carico dettaglio...</strong></div>;

  return (
    <section className="module-section">
      <div className="table-toolbar"><span>{sites.length} cantieri di coordinamento CSE/CSP</span><SortMenu options={COORD_SORTS} sort={sort} onPick={apply} testId="coord-sort" /><label className="bulk-select-all"><input type="checkbox" data-testid="coord-select-all" checked={!!sites.length && selected.length === sites.length} onChange={(e) => replace(e.target.checked ? sites.map(s => s.id) : [])} />Seleziona tutti</label></div>
      {err && <div className="error" style={{margin:20}}>{err}</div>}
      <div style={{padding:"0 20px"}}><BulkDeleteBar collection="sites" ids={selected} testId="coord-bulk" onClear={clear} onDeleted={() => { setOpenSite(null); loadList(); }} /></div>
      <div style={{padding:sites.length ? 0 : "0"}}>
        {ordered.length ? ordered.map((s, i) => {
          const total = (s.status_counts?.OK || 0) + (s.status_counts?.MISSING || 0) + (s.status_counts?.EXPIRED || 0);
          const overall = s.status_counts?.EXPIRED ? "EXPIRED" : s.status_counts?.MISSING ? "MISSING" : "OK";
          return (
            <div key={s.id} data-testid={`coord-site-${i}`} onClick={() => setOpenSite(s.id)} style={{padding:"18px 24px",borderTop:"1px solid var(--line)",display:"grid",gridTemplateColumns:"30px 40px 1fr auto auto",gap:16,alignItems:"center",cursor:"pointer",transition:"background .1s"}} onMouseEnter={(e)=>e.currentTarget.style.background="var(--bg)"} onMouseLeave={(e)=>e.currentTarget.style.background="transparent"}>
              <span className="bulk-check" onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label={`Seleziona ${s.name || "cantiere"}`} data-testid={`coord-select-${i}`} checked={selected.includes(s.id)} onChange={(e) => toggle(s.id, e.target.checked)} /></span>
              <span style={{width:40,height:40,borderRadius:10,background:"var(--wine-soft)",color:"var(--wine)",display:"flex",alignItems:"center",justifyContent:"center"}}><HardHat size={19} /></span>
              <div>
                <strong style={{display:"block",fontSize:14,fontWeight:600,color:"var(--ink)",marginBottom:3}}>{s.client_name || "Committente da associare"}</strong>
                <span style={{fontSize:12,color:"var(--muted)"}}>{s.name || "Cantiere senza nome"}{s.address ? ` · ${s.address}` : ""} · {s.companies_count} imprese</span>
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                {s.status_counts?.OK > 0 && <span style={{fontSize:11,color:"var(--green)",fontWeight:600}}>● {s.status_counts.OK} OK</span>}
                {s.status_counts?.MISSING > 0 && <span style={{fontSize:11,color:"var(--amber)",fontWeight:600}}>● {s.status_counts.MISSING} incomplete</span>}
                {s.status_counts?.EXPIRED > 0 && <span style={{fontSize:11,color:"var(--red)",fontWeight:600}}>● {s.status_counts.EXPIRED} scadute</span>}
                {total === 0 && <span style={{fontSize:11,color:"var(--muted)"}}>Nessuna impresa</span>}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                {total > 0 && <StatusDot status={overall} />}
                <ChevronRight size={16} color="var(--muted)" />
              </div>
            </div>
          );
        }) : (
          <div className="empty" style={{padding:80}}>
            <HardHat size={30} color="var(--wine)" />
            <strong>Nessun cantiere in coordinamento</strong>
            <span>Vai su Cantieri, crea o modifica un cantiere e attiva "Cantiere in coordinamento (CSE/CSP)".</span>
          </div>
        )}
      </div>
    </section>
  );
}

function CoordinationDetail({ detail, catalog, user, back, reload }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editReqs, setEditReqs] = useState(null);
  const [editCompany, setEditCompany] = useState(null);
  const [declarant, setDeclarant] = useState(null);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [aiFlash, setAiFlash] = useState("");
  const { selected: pickedCompanies, toggle: toggleCompany, clear: clearCompanies } = useSelection();
  const emptyCompany = { name: "", role: "esecutrice", address: "", vat_number: "", cciaa: "", durc_number: "", durc_expiry: "", patente_crediti_number: "", patente_crediti_expiry: "", notes: "", required_docs: defaultRequirements() };
  const [form, setForm] = useState(emptyCompany);

  async function createCompany(e) {
    e.preventDefault();
    try {
      await api("/site_companies", { method: "POST", body: JSON.stringify({ data: { ...form, site_id: detail.site.id, name: form.name } }) });
      setShowAdd(false); setForm(emptyCompany);
      reload();
    } catch (err) { alert(err.message); }
  }

  async function saveRequirements() {
    try {
      await api(`/site_companies/${editReqs.id}`, { method: "PUT", body: JSON.stringify({ data: { required_docs: editReqs.required_docs } }) });
      setEditReqs(null); reload();
    } catch (err) { alert(err.message); }
  }

  async function saveCompany(e) {
    e.preventDefault();
    const { id, name, role, address, vat_number, cciaa, durc_number, durc_expiry, patente_crediti_number, notes } = editCompany;
    try {
      await api(`/site_companies/${id}`, { method: "PUT", body: JSON.stringify({ data: { name, role, address, vat_number, cciaa, durc_number, durc_expiry, patente_crediti_number, notes } }) });
      setEditCompany(null); reload();
    } catch (err) { alert(err.message); }
  }

  async function openDeclarant() {
    try { setDeclarant(await api("/settings/declarant")); } catch (err) { alert(err.message); }
  }

  async function saveDeclarant(e) {
    e.preventDefault();
    try {
      await api("/settings/declarant", { method: "PUT", body: JSON.stringify(declarant) });
      setDeclarant(null);
      alert("Dati del dichiarante aggiornati: saranno usati nei prossimi verbali e lettere.");
    } catch (err) { alert(err.message); }
  }

  async function removeCompany(id) {
    if (!window.confirm("Rimuovere questa impresa dal cantiere?")) return;
    await api(`/site_companies/${id}`, { method: "DELETE" });
    reload();
  }

  async function uploadDoc(companyId, docType, file, expiry) {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("collection", "safety");
    fd.append("record_id", detail.site.id);
    fd.append("company_id", companyId || "");
    fd.append("doc_type", docType);
    if (expiry) fd.append("expiry_date", expiry);
    const uploaded = await apiUpload("/files/upload", fd);
    if (!expiry && uploaded.ai_extracted && uploaded.expiry_date) {
      setAiFlash(`✨ AI ha estratto automaticamente la scadenza: ${uploaded.expiry_date} (confidence: ${uploaded.ai_extracted.confidence || "n/a"})`);
      setTimeout(() => setAiFlash(""), 5000);
    }
    reload();
  }
  async function uploadSiteDoc(docType, file, expiry) {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("collection", "site_safety");
    fd.append("record_id", detail.site.id);
    fd.append("doc_type", docType);
    if (expiry) fd.append("expiry_date", expiry);
    const uploaded = await apiUpload("/files/upload", fd);
    if (!expiry && uploaded.ai_extracted && uploaded.expiry_date) {
      setAiFlash(`✨ AI ha estratto automaticamente la scadenza: ${uploaded.expiry_date}`);
      setTimeout(() => setAiFlash(""), 5000);
    }
    reload();
  }

  async function removeFile(id) {
    if (!window.confirm("Rimuovere il documento?")) return;
    await api(`/files/${id}`, { method: "DELETE" });
    reload();
  }

  const authorizedCount = detail.companies.filter((c) => c.authorized_entry).length;
  const blockedCount = detail.companies.length - authorizedCount;

  return (
    <section>
      <div style={{marginBottom:22,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:14}}>
        <button data-testid="coord-back" onClick={back} className="ghost"><ChevronRight size={14} style={{transform:"rotate(180deg)"}} />Torna ai cantieri</button>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          {user.role === "admin" && <button className="ghost" data-testid="declarant-settings-button" onClick={openDeclarant}><UserCog size={15} />Dati dichiarante</button>}
          <DocumentButton label="Report idoneità imprese" testId="idoneita-report" icon={<ShieldAlert size={14} />}
            onGenerate={fmt => downloadFile(`/coordination/${detail.site.id}/idoneita-report?fmt=${fmt}`, `Verifica idoneita imprese - ${detail.site.name}.${fmt}`)} />
          <button className="primary" data-testid="add-company-button" onClick={() => setShowAdd(true)}><Plus size={15} />Nuova impresa</button>
        </div>
      </div>

      <div style={{background:"var(--card)",border:"1px solid var(--line)",borderRadius:14,padding:"22px 26px",marginBottom:18}}>
        <p className="eyebrow"><ShieldAlert size={12} style={{display:"inline"}} /> COORDINATORE SICUREZZA · ALLEGATO XVII</p>
        <h2 style={{font:"600 22px var(--display)",margin:"4px 0 6px",letterSpacing:"-.02em"}}>{detail.site.name}</h2>
        <p className="muted" style={{margin:0}}>{detail.site.address || "Indirizzo non specificato"} · {detail.companies.length} imprese registrate</p>
        {detail.companies.length > 0 && (
          <div style={{marginTop:14,display:"flex",gap:10,flexWrap:"wrap"}}>
            <span style={{fontSize:11,padding:"5px 11px",background:"#E5F1EB",color:"var(--green)",borderRadius:10,fontWeight:600,display:"inline-flex",gap:5,alignItems:"center"}}><CheckCircle2 size={12} /> {authorizedCount} autorizzate all'ingresso</span>
            {blockedCount > 0 && <span style={{fontSize:11,padding:"5px 11px",background:"#FCE9E7",color:"var(--red)",borderRadius:10,fontWeight:600,display:"inline-flex",gap:5,alignItems:"center"}}><ShieldAlert size={12} /> {blockedCount} con ingresso bloccato</span>}
          </div>
        )}
      </div>

      {aiFlash && <div className="flash" data-testid="ai-expiry-flash" style={{marginBottom:18}}>{aiFlash}</div>}

      {detail.companies.length > 0 && <SiteVerify siteId={detail.site.id} onSaved={reload} />}

      <BulkDeleteBar collection="site_companies" ids={pickedCompanies} testId="company-bulk" onClear={clearCompanies} onDeleted={reload} />

      {detail.companies.length ? detail.companies.map((c, i) => (
        <CompanyCard key={c.company.id} data={c} idx={i} catalog={catalog} siteId={detail.site.id} picked={pickedCompanies.includes(c.company.id)} onPick={(on) => toggleCompany(c.company.id, on)} expanded={expandedCompany === c.company.id} onToggle={() => setExpandedCompany(expandedCompany === c.company.id ? null : c.company.id)} onUpload={uploadDoc} onRemoveFile={removeFile} onSaved={reload} onRemove={() => removeCompany(c.company.id)} onEditRequirements={() => setEditReqs({ ...c.company, required_docs: c.company.required_docs?.length ? c.company.required_docs : c.checklist.map(item => ({ doc_type: item.doc_type, label: item.label, mandatory: item.mandatory })) })} onEditCompany={() => setEditCompany({ address: "", vat_number: "", cciaa: "", durc_number: "", durc_expiry: "", patente_crediti_number: "", notes: "", ...c.company })} user={user} />
      )) : (
        <div className="empty" style={{padding:60,background:"#fff",border:"1px solid var(--line)",borderRadius:14,marginBottom:18}}>
          <Users size={26} /><strong>Nessuna impresa registrata</strong><span>Aggiungi le imprese esecutrici, affidatarie, subappaltatrici e i lavoratori autonomi che opereranno in cantiere.</span>
        </div>
      )}

      <ClientSection siteId={detail.site.id} />

      <SiteChecklist detail={detail} onUpload={uploadSiteDoc} onRemoveFile={removeFile} />

      <CoordinationLogs siteId={detail.site.id} logs={detail.logs || []} reload={reload} user={user} />

      {showAdd && (
        <div className="modal-backdrop">
          <form className="create-modal" style={{maxHeight:"88vh",overflowY:"auto"}} onSubmit={createCompany}>
            <button type="button" className="modal-close" onClick={() => setShowAdd(false)}><X /></button>
            <p className="eyebrow">NUOVA IMPRESA</p>
            <h2>Aggiungi impresa al cantiere</h2>
            <label>Ragione sociale<input data-testid="company-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></label>
            <label>Ruolo<select data-testid="company-role" value={form.role} onChange={(e) => setForm({...form, role: e.target.value})}>
              <option value="esecutrice">Impresa esecutrice</option>
              <option value="affidataria">Impresa affidataria</option>
              <option value="subappaltatrice">Impresa subappaltatrice</option>
              <option value="autonomo">Lavoratore autonomo</option>
            </select></label>
            <label>Sede / indirizzo<input data-testid="company-address" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} placeholder="Via, civico, comune (PR)" /></label>
            <label>P. IVA / Codice fiscale<input data-testid="company-vat" value={form.vat_number} onChange={(e) => setForm({...form, vat_number: e.target.value})} /></label>
            <label>N° CCIAA<input value={form.cciaa} onChange={(e) => setForm({...form, cciaa: e.target.value})} /></label>
            <label>N° DURC<input value={form.durc_number} onChange={(e) => setForm({...form, durc_number: e.target.value})} /></label>
            <label>Scadenza DURC<input type="date" value={form.durc_expiry} onChange={(e) => setForm({...form, durc_expiry: e.target.value})} /></label>
            <label>N° Patente crediti<input value={form.patente_crediti_number} onChange={(e) => setForm({...form, patente_crediti_number: e.target.value})} /></label>
            <label>Note<input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></label>
            <RequirementsEditor value={form.required_docs} onChange={(required_docs) => setForm({...form, required_docs})} prefix="company-req" />
            <button className="primary wide" data-testid="company-submit">Aggiungi impresa <ChevronRight size={15} /></button>
          </form>
        </div>
      )}

      {editReqs && (
        <div className="modal-backdrop">
          <div className="create-modal" style={{maxHeight:"88vh",overflowY:"auto"}}>
            <button type="button" className="modal-close" onClick={() => setEditReqs(null)}><X /></button>
            <p className="eyebrow">REQUISITI RICHIESTI</p>
            <h2>{editReqs.name}</h2>
            <RequirementsEditor value={editReqs.required_docs} onChange={(required_docs) => setEditReqs({...editReqs, required_docs})} prefix="edit-req" />
            <button className="primary wide" data-testid="edit-requirements-save" onClick={saveRequirements}>Salva requisiti <ChevronRight size={15} /></button>
          </div>
        </div>
      )}

      {editCompany && (
        <div className="modal-backdrop">
          <form className="create-modal" style={{maxHeight:"88vh",overflowY:"auto"}} onSubmit={saveCompany}>
            <button type="button" className="modal-close" onClick={() => setEditCompany(null)}><X /></button>
            <p className="eyebrow">MODIFICA IMPRESA</p>
            <h2>Dati dell'impresa</h2>
            <label>Ragione sociale<input data-testid="edit-company-name" required value={editCompany.name || ""} onChange={(e) => setEditCompany({...editCompany, name: e.target.value})} /></label>
            <label>Ruolo<select data-testid="edit-company-role" value={editCompany.role || "esecutrice"} onChange={(e) => setEditCompany({...editCompany, role: e.target.value})}>
              <option value="esecutrice">Impresa esecutrice</option>
              <option value="affidataria">Impresa affidataria</option>
              <option value="subappaltatrice">Impresa subappaltatrice</option>
              <option value="autonomo">Lavoratore autonomo</option>
            </select></label>
            <label>Sede / indirizzo<input data-testid="edit-company-address" value={editCompany.address || ""} onChange={(e) => setEditCompany({...editCompany, address: e.target.value})} placeholder="Via, civico, comune (PR)" /></label>
            <label>P. IVA / Codice fiscale<input data-testid="edit-company-vat" value={editCompany.vat_number || ""} onChange={(e) => setEditCompany({...editCompany, vat_number: e.target.value})} /></label>
            <label>N° CCIAA<input value={editCompany.cciaa || ""} onChange={(e) => setEditCompany({...editCompany, cciaa: e.target.value})} /></label>
            <label>N° DURC<input value={editCompany.durc_number || ""} onChange={(e) => setEditCompany({...editCompany, durc_number: e.target.value})} /></label>
            <label>Scadenza DURC<input type="date" value={editCompany.durc_expiry || ""} onChange={(e) => setEditCompany({...editCompany, durc_expiry: e.target.value})} /></label>
            <label>N° Patente crediti<input value={editCompany.patente_crediti_number || ""} onChange={(e) => setEditCompany({...editCompany, patente_crediti_number: e.target.value})} /></label>
            <label>Note<input value={editCompany.notes || ""} onChange={(e) => setEditCompany({...editCompany, notes: e.target.value})} /></label>
            <button className="primary wide" data-testid="edit-company-save">Salva impresa <ChevronRight size={15} /></button>
          </form>
        </div>
      )}

      {declarant && (
        <div className="modal-backdrop">
          <form className="create-modal" style={{maxHeight:"88vh",overflowY:"auto"}} onSubmit={saveDeclarant}>
            <button type="button" className="modal-close" onClick={() => setDeclarant(null)}><X /></button>
            <p className="eyebrow">DATI DICHIARANTE</p>
            <h2>Responsabile dei lavori</h2>
            <p className="muted" style={{fontSize:12,margin:"0 0 6px"}}>Questi dati compaiono nel verbale di accertamento requisiti e nella lettera di richiesta documenti.</p>
            <label>Nome e titolo<input data-testid="declarant-name" required value={declarant.name || ""} onChange={(e) => setDeclarant({...declarant, name: e.target.value})} placeholder="Ing. Nome Cognome" /></label>
            <label>Dati di nascita<input data-testid="declarant-birth" value={declarant.birth || ""} onChange={(e) => setDeclarant({...declarant, birth: e.target.value})} placeholder="nato a … il …" /></label>
            <label>Codice fiscale<input data-testid="declarant-fiscal-code" value={declarant.fiscal_code || ""} onChange={(e) => setDeclarant({...declarant, fiscal_code: e.target.value})} /></label>
            <label>Residenza<input data-testid="declarant-residence" value={declarant.residence || ""} onChange={(e) => setDeclarant({...declarant, residence: e.target.value})} placeholder="residente alla Via …" /></label>
            <label>Studio<input data-testid="declarant-office" value={declarant.office || ""} onChange={(e) => setDeclarant({...declarant, office: e.target.value})} placeholder="con studio in … alla Via …" /></label>
            <label>Iscrizione all'ordine<input data-testid="declarant-register" value={declarant.register || ""} onChange={(e) => setDeclarant({...declarant, register: e.target.value})} placeholder="iscritto all'ordine degli Ingegneri di … n° …" /></label>
            <label>Polizza responsabilità civile professionale<input data-testid="declarant-policy" value={declarant.policy || ""} onChange={(e) => setDeclarant({...declarant, policy: e.target.value})} placeholder="Unipol Sai Assicurazioni n° …" /></label>
            <label>Luogo di firma<input data-testid="declarant-place" value={declarant.place || ""} onChange={(e) => setDeclarant({...declarant, place: e.target.value})} placeholder="Nardò" /></label>
            <button className="primary wide" data-testid="declarant-save">Salva dati dichiarante <ChevronRight size={15} /></button>
          </form>
        </div>
      )}
    </section>
  );
}

function CoordinationLogs({ siteId, logs, reload, user }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0,10), subject: "", description: "", prescriptions: withRowIds([{ text: "", due_date: "" }]) });

  function addPrescription() { setForm({ ...form, prescriptions: [...form.prescriptions, { _uid: newRowId(), text: "", due_date: "" }] }); }
  function updatePrescription(i, k, v) {
    const p = [...form.prescriptions]; p[i] = { ...p[i], [k]: v }; setForm({ ...form, prescriptions: p });
  }
  function removePrescription(i) { setForm({ ...form, prescriptions: form.prescriptions.filter((_, idx) => idx !== i) }); }

  async function submit(e) {
    e.preventDefault();
    const cleaned = stripRowIds(form.prescriptions.filter(p => p.text.trim())).map(p => ({ ...p, status: "aperta" }));
    try {
      await api("/coordination_logs", { method: "POST", body: JSON.stringify({ data: { ...form, prescriptions: cleaned, site_id: siteId, name: form.subject } }) });
      setShowAdd(false);
      setForm({ date: new Date().toISOString().slice(0,10), subject: "", description: "", prescriptions: withRowIds([{ text: "", due_date: "" }]) });
      reload();
    } catch (err) { alert(err.message); }
  }

  async function closePresc(logId, idx) {
    try {
      await api(`/coordination/${siteId}/logs/${logId}/close-prescription/${idx}`, { method: "POST" });
      reload();
    } catch (err) { alert(err.message); }
  }

  async function removeLog(id) {
    if (!window.confirm("Eliminare il verbale?")) return;
    await api(`/coordination_logs/${id}`, { method: "DELETE" });
    reload();
  }

  return (
    <div style={{background:"var(--card)",border:"1px solid var(--line)",borderRadius:14,marginTop:22,overflow:"hidden"}}>
      <div style={{padding:"18px 24px",borderBottom:"1px solid var(--line)",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
        <div>
          <p className="eyebrow">COORDINAMENTO</p>
          <h3 style={{font:"600 17px var(--display)",margin:"3px 0 3px",letterSpacing:"-.02em",color:"var(--ink)"}}>Verbali e prescrizioni</h3>
          <p className="muted" style={{margin:0,fontSize:12}}>Registra gli incontri di coordinamento e traccia le prescrizioni fino alla loro chiusura</p>
        </div>
        <button className="primary" data-testid="add-log-button" onClick={() => setShowAdd(true)}><Plus size={14} />Nuovo verbale</button>
      </div>
      {logs.length ? logs.map((log, i) => {
        const totalP = log.prescriptions?.length || 0;
        const openP = (log.prescriptions || []).filter(p => p.status !== "chiusa").length;
        return (
          <div key={log.id} data-testid={`log-${i}`} style={{padding:"18px 24px",borderTop:"1px solid var(--line)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4,flexWrap:"wrap"}}>
                  <span style={{fontSize:11,padding:"3px 9px",background:"var(--wine-soft)",color:"var(--wine)",borderRadius:8,fontWeight:600}}>{log.date || (log.created_at || "").slice(0,10)}</span>
                  <strong style={{fontSize:14,fontWeight:600,color:"var(--ink)"}}>{log.subject || log.name || "Verbale coordinamento"}</strong>
                  {totalP > 0 && <span style={{fontSize:10,padding:"2px 8px",background: openP === 0 ? "#E5F1EB" : "#FEF3C7",color: openP === 0 ? "var(--green)" : "var(--amber)",borderRadius:8,fontWeight:600}}>{totalP - openP}/{totalP} chiuse</span>}
                </div>
                {log.description && <p style={{margin:"6px 0 10px",fontSize:12.5,color:"var(--muted)",lineHeight:1.5}}>{log.description}</p>}
                {(log.prescriptions || []).map((p, pi) => (
                  <div key={`${log.id}:${p.text || p.due_date}`} data-testid={`prescription-${i}-${pi}`} style={{padding:"9px 12px",marginTop:6,background:"var(--bg)",borderLeft:`3px solid ${p.status === "chiusa" ? "var(--green)" : "var(--amber)"}`,borderRadius:6,display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{fontSize:12.5,color: p.status === "chiusa" ? "var(--muted)" : "var(--ink)",textDecoration: p.status === "chiusa" ? "line-through" : "none"}}>{p.text}</div>
                      {p.due_date && <div style={{fontSize:10.5,color:"var(--muted)",marginTop:3}}>Scadenza: {p.due_date}{p.closed_at && ` · Chiusa il ${p.closed_at.slice(0,10)} da ${p.closed_by || "—"}`}</div>}
                    </div>
                    {p.status !== "chiusa" && <button data-testid={`close-prescription-${i}-${pi}`} onClick={() => closePresc(log.id, pi)} className="ghost" style={{fontSize:11,padding:"5px 10px",color:"var(--green)",borderColor:"#BCDBC9"}}><CheckCircle2 size={12} /> Chiudi</button>}
                  </div>
                ))}
              </div>
              {(user.role === "admin" || log.created_by === user.id) && <button onClick={() => removeLog(log.id)} className="icon-btn" title="Elimina verbale" data-testid={`delete-log-${i}`}><Trash2 size={14} /></button>}
            </div>
          </div>
        );
      }) : <div className="empty" style={{padding:44}}><History size={26} /><strong>Nessun verbale registrato</strong><span>Registra riunioni di coordinamento, prescrizioni impartite e loro chiusura.</span></div>}

      {showAdd && (
        <div className="modal-backdrop">
          <form className="create-modal" onSubmit={submit} style={{width:"min(560px, 100%)"}}>
            <button type="button" className="modal-close" onClick={() => setShowAdd(false)}><X /></button>
            <p className="eyebrow">NUOVO VERBALE</p>
            <h2>Verbale di coordinamento</h2>
            <label>Data<input data-testid="log-date" type="date" required value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></label>
            <label>Oggetto / Titolo<input data-testid="log-subject" required value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} placeholder="Es. Riunione avvio lavori" /></label>
            <label>Descrizione / Note<input data-testid="log-description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Sintesi dell'incontro" /></label>
            <div style={{marginTop:18,borderTop:"1px solid var(--line)",paddingTop:16}}>
              <div style={{fontSize:11,letterSpacing:.6,color:"var(--muted)",fontWeight:600,textTransform:"uppercase",marginBottom:10}}>Prescrizioni impartite</div>
              {form.prescriptions.map((p, i) => (
                <div key={p._uid} style={{display:"grid",gridTemplateColumns:"1fr 130px 30px",gap:8,marginBottom:8}}>
                  <input data-testid={`presc-text-${i}`} placeholder={`Prescrizione ${i+1}`} value={p.text} onChange={(e) => updatePrescription(i, "text", e.target.value)} style={{border:"1px solid var(--line-strong)",padding:"9px 11px",borderRadius:8,fontSize:13}} />
                  <input data-testid={`presc-date-${i}`} type="date" value={p.due_date} onChange={(e) => updatePrescription(i, "due_date", e.target.value)} style={{border:"1px solid var(--line-strong)",padding:"9px 11px",borderRadius:8,fontSize:13}} />
                  {form.prescriptions.length > 1 && <button type="button" onClick={() => removePrescription(i)} className="icon-btn"><X size={14} /></button>}
                </div>
              ))}
              <button type="button" onClick={addPrescription} className="ghost" data-testid="add-prescription" style={{fontSize:11,padding:"6px 10px"}}><Plus size={12} />Aggiungi prescrizione</button>
            </div>
            <button className="primary wide" data-testid="log-submit">Salva verbale <ChevronRight size={15} /></button>
          </form>
        </div>
      )}
    </div>
  );
}

function CompanyCard({ data, idx, catalog, siteId, expanded, onToggle, onUpload, onRemoveFile, onRemove, onEditRequirements, onEditCompany, onSaved = () => {}, user, picked = false, onPick = () => {} }) {
  const { company, checklist, counts, overall, authorized_entry, blocking_missing = [], blocking_expired = [] } = data;
  const roleLabel = (catalog?.role_labels || {})[company.role] || company.role;
  const [working, setWorking] = useState("");
  async function runDoc(kind, filename, fmt = "pdf") {
    setWorking(kind);
    try { await downloadFile(`/coordination/${siteId}/companies/${company.id}/${kind}?fmt=${fmt}`, filename); }
    catch (err) { alert(err.message); }
    setWorking("");
  }
  return (
    <div data-testid={`company-card-${idx}`} style={{background:"var(--card)",border:`1px solid ${authorized_entry ? "#BCDBC9" : "var(--line)"}`,borderRadius:14,marginBottom:14,overflow:"hidden"}}>
      <div onClick={onToggle} style={{padding:"18px 24px",display:"grid",gridTemplateColumns:"30px 1fr auto auto auto",alignItems:"center",gap:14,cursor:"pointer"}}>
        <span className="bulk-check" onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label={`Seleziona ${company.name}`} data-testid={`company-select-${idx}`} checked={picked} onChange={(e) => onPick(e.target.checked)} /></span>
        <div style={{minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4,flexWrap:"wrap"}}>
            <StatusDot status={overall} />
            <strong style={{fontSize:15,fontWeight:600,color:"var(--ink)"}}>{company.name}</strong>
            <span style={{fontSize:10,padding:"2px 8px",background:"var(--wine-soft)",color:"var(--wine)",borderRadius:8,fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>{roleLabel}</span>
            {authorized_entry ? 
              <span data-testid={`badge-authorized-${idx}`} style={{fontSize:10,padding:"3px 10px",background:"#E5F1EB",color:"var(--green)",borderRadius:10,fontWeight:700,textTransform:"uppercase",letterSpacing:.5,display:"inline-flex",alignItems:"center",gap:4}}><CheckCircle2 size={12} /> Autorizzata all'ingresso</span> :
              <span data-testid={`badge-blocked-${idx}`} style={{fontSize:10,padding:"3px 10px",background:"#FCE9E7",color:"var(--red)",borderRadius:10,fontWeight:700,textTransform:"uppercase",letterSpacing:.5,display:"inline-flex",alignItems:"center",gap:4}}><ShieldAlert size={12} /> Ingresso bloccato</span>
            }
          </div>
          <span style={{fontSize:12,color:"var(--muted)"}}>{company.cciaa && `CCIAA ${company.cciaa} · `}{company.durc_number && `DURC ${company.durc_number}`}{company.notes && ` · ${company.notes}`}</span>
          {!authorized_entry && (blocking_missing.length > 0 || blocking_expired.length > 0) && (
            <div style={{marginTop:8,padding:"8px 12px",background:"#FCE9E7",border:"1px solid #F2C3BE",borderRadius:8,fontSize:11,color:"var(--red)"}}>
              {blocking_missing.length > 0 && <div><b>Mancano:</b> {blocking_missing.slice(0,3).join(", ")}{blocking_missing.length > 3 && ` +${blocking_missing.length-3} altri`}</div>}
              {blocking_expired.length > 0 && <div style={{marginTop:blocking_missing.length ? 3 : 0}}><b>Scaduti:</b> {blocking_expired.join(", ")}</div>}
            </div>
          )}
        </div>
        <div style={{display:"flex",gap:8,flexShrink:0}}>
          <span style={{fontSize:11,color:"var(--green)",fontWeight:600}}>{counts.OK} OK</span>
          <span style={{fontSize:11,color:"var(--amber)",fontWeight:600}}>{counts.MISSING} mancanti</span>
          <span style={{fontSize:11,color:"var(--red)",fontWeight:600}}>{counts.EXPIRED} scaduti</span>
        </div>
        <ChevronRight size={17} color="var(--muted)" style={{transform: expanded ? "rotate(90deg)" : "none",transition:"transform .15s"}} />
      </div>
      {expanded && (
        <div style={{borderTop:"1px solid var(--line)",background:"var(--bg)",padding:"6px 0"}}>
          <div style={{padding:"14px 24px",display:"flex",gap:10,flexWrap:"wrap",borderBottom:"1px solid var(--line)"}}>
            <button onClick={onEditCompany} className="ghost" data-testid={`edit-company-${idx}`}><Pencil size={13} />Modifica impresa</button>
            <button onClick={onEditRequirements} className="ghost" data-testid={`edit-requirements-${idx}`}><ClipboardCheck size={13} />Requisiti richiesti</button>
            <DocumentButton label="Genera verbale CSE" testId={`generate-verbale-${idx}`} icon={<Download size={13} />} disabled={!!working}
              onGenerate={fmt => runDoc("verbale-pdf", `Accertamento requisiti - ${company.name}.${fmt}`, fmt)} />
            <DocumentButton label="Genera lettera richiesta documenti" testId={`generate-letter-${idx}`} icon={<Mail size={13} />} disabled={!!working}
              onGenerate={fmt => runDoc("lettera-pdf", `Richiesta documenti - ${company.name}.${fmt}`, fmt)} />
            {user.role === "admin" && <button onClick={onRemove} className="ghost" style={{color:"var(--red)",marginLeft:"auto"}} data-testid={`remove-company-${idx}`}><Trash2 size={13} />Rimuovi impresa</button>}
          </div>
          <div style={{padding:"12px 24px 4px",fontSize:11,letterSpacing:.6,color:"var(--muted)",fontWeight:600,textTransform:"uppercase"}}>Documentazione richiesta</div>
          <DocRequestBar siteId={siteId} companyId={company.id} testId={`doc-request-${idx}`} />
          <CompanyVerify siteId={siteId} companyId={company.id} checklist={checklist} onSaved={onSaved} testId={`company-verify-${idx}`} />
          {checklist.map((item, i) => <DocRow key={item.doc_type} item={item} companyId={company.id} siteId={siteId} onUpload={onUpload} onRemoveFile={onRemoveFile} onSaved={onSaved} idx={`${idx}-${i}`} />)}
        </div>
      )}
    </div>
  );
}

function DocRow({ item, companyId, siteId, onUpload, onRemoveFile, onSaved = () => {}, idx }) {
  const [expiry, setExpiry] = useState("");
  const [uploading, setUploading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const inputRef = React.useRef();
  async function toggleVerified(mode) {
    setVerifying(true);
    try { await api(`/coordination/${siteId}/companies/${companyId}/doc-state/${item.doc_type}`, { method: "POST", body: JSON.stringify({ mode }) }); onSaved(); }
    catch (err) { alert(err.message); } finally { setVerifying(false); }
  }
  async function handle(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { await onUpload(companyId, item.doc_type, file, expiry); setExpiry(""); e.target.value = ""; } catch (err) { alert(err.message); }
    setUploading(false);
  }
  return (
    <div data-testid={`doc-row-${idx}`} style={{padding:"12px 24px",display:"grid",gridTemplateColumns:"1fr auto auto auto auto auto auto",gap:12,alignItems:"center",borderTop:"1px solid var(--line)"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
        <StatusDot status={item.status} />
        <div style={{minWidth:0}}>
          <div style={{fontSize:13,fontWeight:500,color:"var(--ink)"}}>{item.label} {item.mandatory && <span style={{fontSize:9,color:"var(--red)",marginLeft:4}}>OBBLIGATORIO INGRESSO</span>}</div>
          {item.file && <div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>📎 {item.file.original_filename} {item.file.expiry_date && `· scade ${item.file.expiry_date}`}</div>}
        </div>
      </div>
      <StatusBadge status={item.status} />
      <button className={item.verified ? "verify-toggle on" : "verify-toggle"} title="Segna come verificato" data-testid={`verify-doc-${idx}`}
        onClick={() => toggleVerified(item.verified ? "file" : "verificato")} disabled={verifying}>
        <CheckCircle2 size={13} />{item.verified ? "Verificato" : "Verifica"}
      </button>
      <DocStateSelect siteId={siteId} companyId={companyId} item={item} onSaved={onSaved} testId={`doc-state-${idx}`} />
      <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} placeholder="Scadenza" style={{border:"1px solid var(--line-strong)",padding:"5px 8px",borderRadius:6,fontSize:11,background:"#fff"}} title="Scadenza opzionale" />
      <button className="ghost" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`upload-doc-${idx}`} style={{fontSize:11,padding:"6px 10px"}}>
        <Upload size={12} /> {uploading ? "..." : (item.file ? "Sostituisci" : "Carica")}
      </button>
      <input type="file" ref={inputRef} style={{display:"none"}} onChange={handle} />
      <span style={{display:"flex",gap:4}}>
        {item.file && <>
          <button onClick={() => downloadFile(`/files/${item.file.id}/download`, item.file.original_filename)} className="icon-btn" title="Scarica"><Download size={13} /></button>
          <button onClick={() => onRemoveFile(item.file.id)} className="icon-btn" title="Rimuovi"><Trash2 size={13} /></button>
        </>}
      </span>
    </div>
  );
}

function SiteChecklist({ detail, onUpload, onRemoveFile }) {
  return (
    <div style={{background:"var(--card)",border:"1px solid var(--line)",borderRadius:14,marginTop:22,overflow:"hidden"}}>
      <div style={{padding:"18px 24px",borderBottom:"1px solid var(--line)"}}>
        <p className="eyebrow">DOCUMENTAZIONE DI CANTIERE</p>
        <h3 style={{font:"600 17px var(--display)",margin:"3px 0 3px",letterSpacing:"-.02em",color:"var(--ink)"}}>Documenti specifici del cantiere</h3>
        <p className="muted" style={{margin:0,fontSize:12}}>PiMUS, verifiche INAIL, conformità impianto elettrico, DPI anticaduta, ecc.</p>
      </div>
      {detail.site_checklist.map((item, i) => (
        <SiteDocRow key={item.doc_type} item={item} onUpload={onUpload} onRemoveFile={onRemoveFile} idx={i} />
      ))}
    </div>
  );
}

function SiteDocRow({ item, onUpload, onRemoveFile, idx }) {
  const [expiry, setExpiry] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef();
  async function handle(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { await onUpload(item.doc_type, file, expiry); setExpiry(""); e.target.value = ""; } catch (err) { alert(err.message); }
    setUploading(false);
  }
  return (
    <div data-testid={`site-doc-${idx}`} style={{padding:"14px 24px",display:"grid",gridTemplateColumns:"1fr auto auto auto auto",gap:12,alignItems:"center",borderTop:"1px solid var(--line)"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
        <StatusDot status={item.status} />
        <div style={{minWidth:0}}>
          <div style={{fontSize:13,fontWeight:500,color:"var(--ink)"}}>{item.label}</div>
          {item.file && <div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>📎 {item.file.original_filename} {item.file.expiry_date && `· scade ${item.file.expiry_date}`}</div>}
        </div>
      </div>
      <StatusBadge status={item.status} />
      <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} style={{border:"1px solid var(--line-strong)",padding:"5px 8px",borderRadius:6,fontSize:11,background:"#fff"}} />
      <button className="ghost" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`upload-site-doc-${idx}`} style={{fontSize:11,padding:"6px 10px"}}><Upload size={12} /> {uploading ? "..." : (item.file ? "Sostituisci" : "Carica")}</button>
      <input type="file" ref={inputRef} style={{display:"none"}} onChange={handle} />
      <span style={{display:"flex",gap:4}}>
        {item.file && <>
          <button onClick={() => downloadFile(`/files/${item.file.id}/download`, item.file.original_filename)} className="icon-btn"><Download size={13} /></button>
          <button onClick={() => onRemoveFile(item.file.id)} className="icon-btn"><Trash2 size={13} /></button>
        </>}
      </span>
    </div>
  );
}

export { CoordinationModule };
